import piexif from "piexifjs";

/**
 * Get the device's current position (the school's location) as decimal degrees.
 * Rejects when geolocation is unsupported, denied by the user, or times out.
 */
export function getCurrentCoords() {
  return new Promise((resolve, reject) => {
    if (!("geolocation" in navigator)) {
      const err = new Error("Geolocation is not supported by this browser");
      err.reason = "unsupported";
      reject(err);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        }),
      (err) => {
        err.reason =
          { 1: "denied", 2: "unavailable", 3: "timeout" }[err.code] || "unknown";
        reject(err);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 }
    );
  });
}

/**
 * Turn a getCurrentCoords() rejection into an actionable message for the user.
 * The key case: when the site's location permission is hard-blocked, the
 * browser fails silently without ever showing the permission popup, so the
 * user must unblock it manually — the message has to walk them through that.
 */
export async function getGeoFailureMessage(err) {
  if (err?.reason === "denied") {
    let state = "unknown";
    try {
      if (navigator.permissions?.query) {
        state = (await navigator.permissions.query({ name: "geolocation" })).state;
      }
    } catch (_) {
      /* Permissions API unavailable (older Safari) — fall through */
    }
    if (state === "denied") {
      return "Location access is blocked for this website, so the browser will not show the permission popup. Click the icon on the left side of the address bar, set Location to Allow, reload this page and upload the photo again — or upload a geo-tagged photo.";
    }
    return "Location permission was not granted. Please upload the photo again and click Allow when the browser asks for your location — or upload a geo-tagged photo.";
  }
  if (err?.reason === "unavailable") {
    return "Your device could not find its location. Please turn on Location/GPS in your device settings and allow your browser to use it, then upload the photo again — or upload a geo-tagged photo.";
  }
  if (err?.reason === "timeout") {
    return "Finding your location took too long. Please check that Location/GPS is turned on and upload the photo again — or upload a geo-tagged photo.";
  }
  return "This photo is not geo-tagged. Please allow location access in your browser so your school's location can be added, or upload a geo-tagged photo.";
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

// piexif can only write EXIF into JPEGs, so PNGs are re-encoded to JPEG first.
function pngToJpegDataUrl(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        // transparent PNG areas turn black in JPEG unless painted white first
        ctx.fillStyle = "#fff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/jpeg", 0.92));
      } catch (err) {
        reject(err);
      } finally {
        URL.revokeObjectURL(url);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not decode image"));
    };
    img.src = url;
  });
}

function dataUrlToFile(dataUrl, name, type) {
  const base64 = dataUrl.split(",")[1];
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new File([bytes], name, { type });
}

/**
 * Return a copy of `file` with the given GPS coordinates written into its
 * EXIF, so the stored image itself is geo-tagged. JPEGs keep their format;
 * PNGs come back re-encoded as JPEG (with a .jpg name) because EXIF can't be
 * written into a PNG with piexif.
 */
export async function embedGpsExif(file, latitude, longitude) {
  const isPng = file.type === "image/png";
  const jpegDataUrl = isPng
    ? await pngToJpegDataUrl(file)
    : await fileToDataUrl(file);

  let exifObj;
  try {
    exifObj = piexif.load(jpegDataUrl);
  } catch (err) {
    exifObj = { "0th": {}, Exif: {}, GPS: {}, Interop: {}, "1st": {}, thumbnail: null };
  }
  exifObj.GPS = exifObj.GPS || {};
  exifObj.GPS[piexif.GPSIFD.GPSLatitudeRef] = latitude >= 0 ? "N" : "S";
  exifObj.GPS[piexif.GPSIFD.GPSLatitude] = piexif.GPSHelper.degToDmsRational(Math.abs(latitude));
  exifObj.GPS[piexif.GPSIFD.GPSLongitudeRef] = longitude >= 0 ? "E" : "W";
  exifObj.GPS[piexif.GPSIFD.GPSLongitude] = piexif.GPSHelper.degToDmsRational(Math.abs(longitude));

  const stamped = piexif.insert(piexif.dump(exifObj), jpegDataUrl);
  const name = isPng ? file.name.replace(/\.png$/i, "") + ".jpg" : file.name;
  return dataUrlToFile(stamped, name, "image/jpeg");
}
